import type { SxProps, Theme } from '@mui/material/styles';

// ダーク・グラスモーフィズム + Nissan アクセント (#C71444)。
// 自動車壁紙背景に映えるよう、ダーク半透明カード + フロステッドエフェクトで統一。

export const ACCENT = '#C71444'; // Nissan の公式 brand red（壁紙ページの CSS で確認）
export const ACCENT_HOVER = '#A50F37';
const TEXT_PRIMARY = 'rgba(255, 255, 255, 0.95)';
const TEXT_SECONDARY = 'rgba(255, 255, 255, 0.65)';
const BORDER_DEFAULT = 'rgba(255, 255, 255, 0.16)';
const BORDER_HOVER = 'rgba(255, 255, 255, 0.32)';
const INPUT_BG = 'rgba(255, 255, 255, 0.04)';

export const cardSx: SxProps<Theme> = {
  p: { xs: 4, sm: 5 },
  maxWidth: 440,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  position: 'relative',
  zIndex: 1,
  backgroundColor: 'rgba(15, 20, 35, 0.62)',
  backdropFilter: 'blur(18px) saturate(140%)',
  WebkitBackdropFilter: 'blur(18px) saturate(140%)',
  border: '1px solid rgba(255, 255, 255, 0.10)',
  borderRadius: 3,
  boxShadow:
    '0 24px 64px -16px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.04) inset',
  color: TEXT_PRIMARY,
};

// MUI TextField outlined variant の dark mode 風カスタム。
export const textFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    backgroundColor: INPUT_BG,
    color: TEXT_PRIMARY,
    borderRadius: 2,
    transition: 'background-color 200ms ease',
  },
  '& .MuiInputBase-root:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: BORDER_DEFAULT,
    transition: 'border-color 200ms ease',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: BORDER_HOVER,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
    borderWidth: 1,
  },
  '& .MuiInputLabel-root': {
    color: TEXT_SECONDARY,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: ACCENT,
  },
  '& .MuiFormHelperText-root': {
    color: TEXT_SECONDARY,
    marginLeft: 0.5,
  },
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px rgba(15, 20, 35, 0.62) inset',
    WebkitTextFillColor: TEXT_PRIMARY,
    caretColor: TEXT_PRIMARY,
    transition: 'background-color 5000s ease-in-out 0s',
  },
};

export const primaryButtonSx: SxProps<Theme> = {
  mt: 1,
  py: 1.4,
  borderRadius: 2,
  fontWeight: 600,
  fontSize: '0.95rem',
  letterSpacing: '0.04em',
  textTransform: 'none',
  background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_HOVER} 100%)`,
  color: '#FFFFFF',
  boxShadow: `0 8px 24px -8px ${ACCENT}80`,
  transition: 'all 200ms ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${ACCENT_HOVER} 0%, #7A0828 100%)`,
    boxShadow: `0 12px 32px -8px ${ACCENT}AA`,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(255, 255, 255, 0.35)',
    boxShadow: 'none',
  },
};

export const alertSx: SxProps<Theme> = {
  backgroundColor: 'rgba(199, 20, 68, 0.12)',
  color: '#FFA8B8',
  border: `1px solid ${ACCENT}40`,
  borderRadius: 2,
  '& .MuiAlert-icon': {
    color: ACCENT,
  },
};

export const helperTextSx: SxProps<Theme> = {
  color: TEXT_SECONDARY,
  fontSize: '0.875rem',
  lineHeight: 1.6,
};

// OTP 入力欄を「コードらしく」見せるための専用スタイル。
// 1 つの input のままだが、letter-spacing を広げて等幅にすることで segmented input に近い印象。
export const otpInputSx: SxProps<Theme> = {
  ...textFieldSx,
  '& input': {
    textAlign: 'center',
    fontFamily: '"SF Mono", "Roboto Mono", "Menlo", monospace',
    fontSize: '1.6rem',
    fontWeight: 500,
    letterSpacing: '0.8em',
    paddingRight: 'calc(14px - 0.8em)', // letter-spacing で右にずれる分を補正
  },
};
